import { featureFlags } from '@seen/core/featureFlags'
import { invariant } from '@seen/core/invariant'
import type { Participant } from '@seen/core/models/patients/participant'
import { ExtendedQuestionnaireResponse } from '@seen/core/models/questionnaireResponse'
import * as questionnaires from '@seen/core/models/questionnaires/index'
import { IconInfoCircle } from '@tabler/icons-react'
import type { JSX } from 'react'
import { Tooltip } from '#src/components/core/tooltip/Tooltip.tsx'
import { TooltipContent } from '#src/components/core/tooltip/TooltipContent.tsx'
import { TooltipTrigger } from '#src/components/core/tooltip/TooltipTrigger.tsx'
import { IconNotepad } from '#src/components/icons/IconNotepad.tsx'
import {
  FacesheetCard,
  FacesheetCardCollapseTrigger,
  FacesheetCardContent,
  FacesheetCardHeader,
  FacesheetCardIcon,
  FacesheetCardLabel,
  FacesheetCardTitle,
  reportFacesheetNotepadSaved,
  useFacesheetTab,
} from '#src/components/patient/detail-content/facesheet/index.ts'
import { QuestionnaireItemForm } from '#src/components/questionnaire-form/questionnaire-item-form/QuestionnaireItemForm.tsx'
import { useFeatureFlag } from '#src/hooks/useFeatureFlag.ts'
import { useSearchOne } from '#src/hooks/useSearchOne.ts'

export interface NotepadCardProps {
  participant: Participant
}

export function NotepadCard({ participant }: NotepadCardProps): JSX.Element {
  const facesheetImprovements = useFeatureFlag(
    featureFlags.facesheetImprovements
  )
  const facesheetTab = useFacesheetTab()
  const headerNoteItem =
    questionnaires.AdditionalInformationQuestionnaire.item.find(
      (item) => item.linkId === 'header-note'
    )
  invariant(
    headerNoteItem,
    'Header note item not found in Additional Information Questionnaire'
  )

  const questionnaireResponseResult = useSearchOne(
    ExtendedQuestionnaireResponse,
    ExtendedQuestionnaireResponse.searchQuery({
      questionnaire: questionnaires.AdditionalInformationQuestionnaire,
      subject: participant.reference,
    })
  )
  const questionnaireResponse = questionnaireResponseResult.data ?? null

  return (
    <FacesheetCard rumName="notepad">
      <FacesheetCardHeader>
        <FacesheetCardTitle>
          <FacesheetCardIcon>
            <IconNotepad />
          </FacesheetCardIcon>
          <FacesheetCardLabel>Notepad</FacesheetCardLabel>
          <FacesheetCardCollapseTrigger />
          {facesheetImprovements && (
            <Tooltip>
              <TooltipTrigger
                aria-label="Who can see the notepad"
                className="opacity-0 group-hover/facesheet-card:opacity-100 focus-visible:opacity-100"
              >
                <IconInfoCircle className="size-3" />
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                <p>Notes visible to everyone</p>
              </TooltipContent>
            </Tooltip>
          )}
        </FacesheetCardTitle>
      </FacesheetCardHeader>
      <FacesheetCardContent>
        <QuestionnaireItemForm
          item={headerNoteItem}
          questionnaireResponse={questionnaireResponse}
          className="text-foreground-secondary"
          onSaved={
            facesheetImprovements
              ? () =>
                  reportFacesheetNotepadSaved({
                    participantId: participant.id,
                    tab: facesheetTab?.tab,
                    notepad: 'shared',
                  })
              : undefined
          }
          placeholder={
            facesheetImprovements ? 'Write a note...' : 'Start writing...'
          }
        />
      </FacesheetCardContent>
    </FacesheetCard>
  )
}
